import { SECTION_LABEL } from '@/components/common/surfaces';
import { FindingRow } from '@/components/review/FindingRow';
import type { Category, CategorySummary, ExportMode, FindingModel } from '@/types/documents.types';
import { findingCategories, findingDisplayCategory, isFindingInExport } from '@/utils/findings';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';

interface FindingsListProps {
  findings: FindingModel[];
  /** The session's category snapshot. */
  categories: CategorySummary[];
  excludedCategories: ReadonlySet<Category>;
  pageNumber: number;
  mode: ExportMode;
  selectedFindingId: string | null;
  savingFindingId: string | null;
  /** Holds every "Ignorovat" / "Vrátit" (the re-run of the skipped pages is going). */
  disabled?: boolean;
  onSelect: (findingId: string) => void;
  onToggle: (finding: FindingModel) => void;
}

/** The review's right column, "Detekované údaje": the current page's findings in page order. */
export const FindingsList: FC<FindingsListProps> = ({
  findings,
  categories,
  excludedCategories,
  pageNumber,
  mode,
  selectedFindingId,
  savingFindingId,
  disabled = false,
  onSelect,
  onToggle,
}) => {
  const { t } = useTranslation();

  return (
    <div className="flex min-h-0 flex-col border-l border-line bg-white max-[860px]:border-l-0 max-[860px]:border-t">
      <div className="flex-none border-b border-hairline px-4 pb-3 pt-4">
        <div className="flex items-baseline justify-between">
          <div className={SECTION_LABEL}>{t('review.findings.title')}</div>
          <div className="text-[11.5px] text-faint">{t('review.findings.page', { page: pageNumber })}</div>
        </div>
        <div className="mt-1.5 text-[11.5px] leading-[1.5] text-subtle">{t('review.findings.hint')}</div>
      </div>
      <div className="relative flex-1 overflow-auto px-2.5 pb-4 pt-1.5">
        {findings.length === 0 ? (
          <p className="m-0 px-2 py-2.5 text-[11.5px] leading-[1.5] text-faint">{t('review.findings.empty')}</p>
        ) : (
          <ul className="m-0 list-none p-0">
            {findings.map((finding) => {
              const own = findingCategories(finding);
              return (
                <FindingRow
                  key={finding.id}
                  finding={finding}
                  categories={categories.filter((entry) => own.includes(entry.key))}
                  display={findingDisplayCategory(finding, categories, excludedCategories)}
                  pageNumber={pageNumber}
                  mode={mode}
                  inExport={isFindingInExport(finding, excludedCategories)}
                  selected={finding.id === selectedFindingId}
                  isSaving={finding.id === savingFindingId}
                  disabled={disabled}
                  onSelect={onSelect}
                  onToggle={onToggle}
                />
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};
